import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbToast } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs/operators';
import { ButtonComponent } from '../button';
import { TextEllipsisComponent } from '../text-ellipsis';
import { MbsPopupIcon, MbsPopupType, MbsSize, TextBreak, isTemplate } from '../utils';
import { SafePipe } from '../utils/pipes';
import Toast, { Button, DelayConfig } from './toast.model';
import { ToastService } from './toast.service';

/**
 * Default delay (in milliseconds) by color type schema
 */
const DEFAULT_DELAY_CONFIG: DelayConfig = {
  [MbsPopupType.info]: 3000,
  [MbsPopupType.success]: 3000,
  [MbsPopupType.warning]: 5000,
  [MbsPopupType.danger]: 10000
};

@Component({
  selector: 'mbs-toasts',
  templateUrl: './toast.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, NgClass, NgbToast, NgTemplateOutlet, SafePipe, TextEllipsisComponent],
  host: {
    '[class.toast-container]': 'true',
    'aria-live': 'polite',
    'aria-atomic': 'true'
  }
})
export class ToastComponent {
  private toastService = inject(ToastService);

  public readonly MbsSize = MbsSize;
  public readonly isTemplate = isTemplate;
  public readonly TextBreak = TextBreak;

  private readonly toasts = signal<Toast[]>([]);

  /**
   * If `true` - Toast will be hidden after few seconds.
   */
  public readonly autohide = model(true);

  /**
   * Max number of visible toasts. By default - 3
   */
  public readonly max = input(3);

  /**
   * Custom delay by color type schema, merged over `DEFAULT_DELAY_CONFIG`
   */
  public readonly delayConfig = input<Partial<DelayConfig>>({});

  /**
   * Delay by color type schema, with the custom config applied
   */
  protected readonly delays = computed<DelayConfig>(() => ({ ...DEFAULT_DELAY_CONFIG, ...this.delayConfig() }));

  /**
   * Toasts for display, but no more than `max` quantity
   */
  public readonly visibleToasts = computed(() => this.toasts().slice(0, this.max()));

  constructor() {
    this.toastService
      .getQueue()
      .pipe(
        filter((t) => t.toast != null),
        takeUntilDestroyed()
      )
      .subscribe(({ action, toast }) => {
        if (action === 'add') {
          this.handleAdd(toast);
        } else if (action === 'remove') {
          this.handleRemove(toast);
        }
      });
  }

  /**
   * Add toast
   * @param {Toast} toast
   */
  handleAdd(toast: Toast): void {
    this.toasts.update((toasts) => [...toasts, toast]);
  }

  /**
   * Remove toast from visible
   * @param {Toast} toast
   */
  handleRemove(toast: Toast): void {
    this.toasts.update((toasts) => toasts.filter((t) => t !== toast));
  }

  /**
   * Custom classes for toast
   * @param {Toast} toast
   * @param {boolean} isLast
   * @return {string}
   */
  getToastClasses(toast: Toast, isLast: boolean): string {
    const type = toast.type || MbsPopupType.info;
    const classes = ['mbs-toast', `mbs-toast-${type}`];

    isLast && classes.push('-old');
    toast.header && !toast.showClose && classes.push('-noClose');
    toast.classname && classes.push(toast.classname);

    return classes.join(' ');
  }

  /**
   * Adding classes for default icon by type or custom icon. Used in header of toast
   * @param {Toast} toast
   * @return {string}
   */
  getIconClasses(toast: Toast): string {
    const type = toast.type || MbsPopupType.info;
    const classes = ['mbs-toast-ico', `text-${MbsPopupType[type]}`];
    const iconClasses = toast.icon && typeof toast.icon === 'string' ? toast.icon : MbsPopupIcon[type];

    classes.push(iconClasses);

    return classes.join(' ');
  }

  public onButtonClick(button: Button, toast: Toast): void {
    button.clickHandler();
    !toast.preventClose && this.handleRemove(toast);
  }
}
